// pages/signature/modals/tabs/rules.ts

export type EnvelopeStatusNorm = "draft" | "running" | "canceled" | "closed" | "other";
export type ParticipantType = "signer" | "witness" | "observers";

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

export function normalizeEnvelopeStatus(raw?: string | null): EnvelopeStatusNorm {
	const n = norm(raw);
	if (n === "draft" || n === "rascunho") return "draft";
	if (n === "running" || n === "em_progresso" || n === "em progresso" || n === "em_andamento") return "running";
	if (n === "canceled" || n === "cancelado" || n === "cancelada") return "canceled";
	if (n === "closed" || n === "assinado" || n === "finalizado" || n === "concluido" || n === "concluído") return "closed";
	return "other";
}

export type AddPermissions = {
	signer: boolean;
	witness: boolean;
	observers: boolean;
	inputsDisabled: boolean; // true => trava inputs/botão
	defaultType: Exclude<ParticipantType, "witness">; // "signer" | "observers"
};

/** Regras:
 *  - draft:    pode signer, witness, observers
 *  - running:  só observers
 *  - canceled/closed/other: nada
 */
export function computeAddPermissions(status: EnvelopeStatusNorm): AddPermissions {
	const canSignerWitness = status === "draft";
	const canObserver = status === "draft" || status === "running";
	const inputsDisabled = !(canSignerWitness || canObserver);
	const defaultType = canSignerWitness ? "signer" : "observers";
	return {
		signer: canSignerWitness,
		witness: canSignerWitness,
		observers: canObserver,
		inputsDisabled,
		defaultType,
	};
}

/* -------- Normalizadores de participante -------- */
export function normalizeParticipantType(raw?: string | null): ParticipantType {
	const n = norm(raw);
	if (n === "signer") return "signer";
	if (n === "witness") return "witness";
	return "observers";
}

export const isObserverType = (t?: string | null) => normalizeParticipantType(t) === "observers";
export const isSignerOrWitnessType = (t?: string | null) => {
	const n = normalizeParticipantType(t);
	return n === "signer" || n === "witness";
};

/* -------- Normalizadores utilitários -------- */
export const normalizeCpf = (s: string) => (s || "").replace(/\D+/g, "");
export const normalizeEmail = (s: string) => (s || "").trim().toLowerCase();

/** Duplicidade por CPF OU e-mail */
export function hasDuplicateCpfOrEmail(people: Array<{ document?: string | null; email?: string | null }>, cpf: string, email: string) {
	const cpfN = normalizeCpf(cpf);
	const emailN = normalizeEmail(email);
	const dupCpf = people.some((p) => normalizeCpf(p.document || "") === cpfN);
	const dupEmail = people.some((p) => normalizeEmail(p.email || "") === emailN);
	return { dupCpf, dupEmail };
}
